import { TextField, Autocomplete, TextFieldProps as MuiTextFieldProps, Popper, Stack, styled, Typography, CircularProgress } from '@mui/material';
import { InvoiceRowDto } from '@sr/dto';
import { useInvoiceRowsNotPurchase } from 'entities/invoice-rows/invoice-rows.hook';
import { fieldToTextField, TextFieldProps } from 'formik-mui';
import { HTMLAttributes, ReactNode, SyntheticEvent, useCallback, useEffect, useState } from 'react';

const RowOptionElement = ({ row, props }: { row: InvoiceRowDto, props: HTMLAttributes<HTMLElement> }) => {
  return (
    <Stack {...props} direction='column' alignItems='flex-start' justifyContent='flex-start' width='100%'>
      <Stack direction="row" spacing={1.1} sx={{ width: '100%' }} alignItems="center" justifyContent="flex-start">
        <Typography align='left'>
          {row.id} {row.media?.name || 'Наименование отсутствует'}
        </Typography>
      </Stack>

    </Stack>
  );
};

const InvoiceRowPopper = styled(Popper)({
  width: '450px !important',
});

type InvoiceRowPickerProps = TextFieldProps & {
  label?: ReactNode;
  disabled?: boolean;
  textFieldProps?: MuiTextFieldProps;
  invoiceId?: number;
};

export const InvoiceRowPicker = ({ label, onChange, disabled, invoiceId, ...props }: InvoiceRowPickerProps) => {
  const { form: { setFieldValue }, field: { name, value } } = props;
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<InvoiceRowDto | null>(null);
  
  const { rows, isLoading, error } = useInvoiceRowsNotPurchase(invoiceId || 0);

  useEffect(() => {
    if (value && rows.length > 0) {
      const foundRow = rows.find(row => row.id === value);
      setSelectedRow(foundRow || null);
    } else if (!value) {
      setSelectedRow(null);
    }
  }, [value, rows]);

  useEffect(() => {
    if (selectedRow && invoiceId) {
      setSelectedRow(null);
      setFieldValue(name, null);
    }
  }, [invoiceId]);

  const handleChange = useCallback(
    (e: SyntheticEvent<Element, Event>, newValue: InvoiceRowDto | null) => {
      setSelectedRow(newValue);
      setFieldValue(name, newValue?.id || null);
      if (onChange) {
        onChange(e as any);
      }
    },
    [setSelectedRow, setFieldValue, name, onChange]
  );

  const isLoadingData = isLoading && open;
  const hasNoOptions = !isLoading && rows.length === 0;
  
  if (error && invoiceId) {
    return (
      <TextField
        {...fieldToTextField(props)}
        label={label || 'Строка счета'}
        error
        helperText={`Ошибка загрузки строк: ${error}`}
        disabled
        fullWidth
      />
    );
  }

  return (
    <Autocomplete
      value={selectedRow}
      autoComplete={false}
      options={rows}
      open={open && !!invoiceId}
      disabled={disabled || !invoiceId}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onChange={handleChange}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={(option) => `${option.media?.name}`}
      loading={isLoadingData}
      loadingText={'Загрузка строк...'}
      noOptionsText={!invoiceId ? 'Сначала выберите счет' : 'Нет строк в выбранном счете'}
      fullWidth
      PopperComponent={(props) => <InvoiceRowPopper {...props} />}
      renderInput={(params) => (
        <TextField
          {...fieldToTextField(props)}
          {...params}
          label={label || 'Строка счета'}
          placeholder={!invoiceId ? 'Выберите счет' : 'Выберите строку счета'}
          disabled={disabled || !invoiceId}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <RowOptionElement key={option.id} row={option} props={props} />
      )}
    />
  );
};